import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../api/config';
import { ArrowPathIcon, DocumentDuplicateIcon, ShoppingBagIcon, TruckIcon, BanknotesIcon, ShieldCheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

interface MarketplaceAccessStatus {
  tenantAccessStatus: string;
  hasMembership: boolean;
  membershipStatus: string | null;
  role: string | null;
  organisationType: 'buyer' | 'supplier' | 'both' | null;
  capabilities: string[];
  hasProfile: boolean;
  profileStatus: string | null;
  verificationReason: string | null;
}

const OrdersPage: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [accessStatus, setAccessStatus] = useState<MarketplaceAccessStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    const checkAccess = async () => {
      try {
        const res = await fetch(`${API_URL}/api/marketplace/b2b/membership-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok) {
          setAccessStatus(data);
        } else if (res.status === 403 || res.status === 404) {
          // Marketplace not enabled for this organisation — show the
          // "not configured" block screen instead of a generic error.
          setAccessStatus({
            tenantAccessStatus: 'not_enabled',
            hasMembership: false,
            membershipStatus: null,
            role: null,
            organisationType: null,
            capabilities: [],
            hasProfile: false,
            profileStatus: null,
            verificationReason: null,
          });
        } else {
          setError(data.error || 'Failed to check marketplace access');
        }
      } catch (err) {
        setError('Failed to verify marketplace access');
      } finally {
        setLoading(false);
      }
    };
    checkAccess();
  }, [token]);

  const isSuperAdmin = user?.role === 'super_admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <ShieldCheckIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to load orders</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Super admin goes to the marketplace admin orders page
  if (isSuperAdmin) {
    navigate('/marketplace/admin/orders', { replace: true });
    return null;
  }

  // No marketplace access at all
  if (!accessStatus || !accessStatus.hasMembership || accessStatus.tenantAccessStatus !== 'enabled') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <ShoppingBagIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Marketplace Orders</h2>
          <p className="text-gray-600 mb-4">
            Your organisation does not have marketplace access configured.
            Contact your administrator to enable marketplace ordering.
          </p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Supplier-only membership
  if (accessStatus.organisationType === 'supplier') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900">Supplier Orders</h1>
              <p className="text-gray-500 mt-1">Manage your supplier orders and fulfilment</p>
            </div>
            <button
              onClick={() => navigate('/marketplace/supplier/orders')}
              className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-lg"
            >
              Open Supplier Orders
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <DocumentDuplicateIcon className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Supplier Orders</span>
              </div>
              <p className="text-2xl font-extrabold text-gray-900">Fulfilment</p>
              <p className="text-sm text-gray-500 mt-1">View and manage orders from buyers</p>
              <button
                onClick={() => navigate('/marketplace/supplier/orders')}
                className="mt-3 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
              >
                View supplier orders →
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <TruckIcon className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Fulfilment</span>
              </div>
              <p className="text-2xl font-extrabold text-gray-900">Shipments</p>
              <p className="text-sm text-gray-500 mt-1">Track deliveries and proof of delivery</p>
              <button
                onClick={() => navigate('/marketplace/supplier/fulfilment')}
                className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                View fulfilment →
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                  <BanknotesIcon className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Finance</span>
              </div>
              <p className="text-2xl font-extrabold text-gray-900">Settlements</p>
              <p className="text-sm text-gray-500 mt-1">View payments and settlement records</p>
              <button
                onClick={() => navigate('/marketplace/supplier/settlements')}
                className="mt-3 text-sm font-semibold text-amber-600 hover:text-amber-700"
              >
                View settlements →
              </button>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
            <h3 className="font-bold text-emerald-900 mb-2">About Supplier Orders</h3>
            <p className="text-sm text-emerald-700 leading-relaxed">
              Supplier orders are created when a buyer places a direct order or accepts a quotation.
              Each order is linked to a parent buyer order and contains the items, quantities, and
              pricing for your tenant. You can confirm, prepare, quality-check, dispatch, and track
              deliveries from the supplier orders page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Buyer-only or dual membership (buyer first)
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">Purchase Orders</h1>
            <p className="text-gray-500 mt-1">Track your orders, deliveries, and invoices</p>
          </div>
          <button
            onClick={() => navigate('/marketplace/buyer/orders')}
            className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-lg"
          >
            Open My Orders
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <DocumentDuplicateIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Orders</span>
            </div>
            <p className="text-2xl font-extrabold text-gray-900">Purchase Orders</p>
            <p className="text-sm text-gray-500 mt-1">View order history and details</p>
            <button
              onClick={() => navigate('/marketplace/buyer/orders')}
              className="mt-3 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
            >
              View my orders →
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <TruckIcon className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Deliveries</span>
            </div>
            <p className="text-2xl font-extrabold text-gray-900">Tracking</p>
            <p className="text-sm text-gray-500 mt-1">Track shipments and delivery status</p>
            <button
              onClick={() => navigate('/marketplace/buyer/deliveries')}
              className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-700"
            >
              View deliveries →
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <BanknotesIcon className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Payments</span>
            </div>
            <p className="text-2xl font-extrabold text-gray-900">Invoices</p>
            <p className="text-sm text-gray-500 mt-1">View and pay invoices</p>
            <button
              onClick={() => navigate('/marketplace/buyer/invoices')}
              className="mt-3 text-sm font-semibold text-amber-600 hover:text-amber-700"
            >
              View invoices →
            </button>
          </div>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
          <h3 className="font-bold text-emerald-900 mb-2">About Purchase Orders</h3>
          <p className="text-sm text-emerald-700 leading-relaxed">
            Purchase orders are created when you place a direct order on the marketplace or accept
            a supplier quotation. Each order contains the items, quantities, pricing, and delivery
            details. Suppliers confirm orders, prepare shipments, and update delivery status. You can
            track everything from orders through to deliveries and invoices in your buyer portal.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
